import React, { useMemo, useRef } from 'react'
import {
  InvoicePreview,
  PurchaseOrderPreview,
  QuotationPreview,
  previewRows
} from './PdfGenerationStudio'
import { useTemplatePreviewSettings } from '../context/TemplatePreviewContext'
import './PdfGenerationStudio.css'

export default function TemplateDocumentPreviewModal({ isOpen, template, title, draft, items, totals, onClose, onDownload }) {
  const safeItems = Array.isArray(items) ? items : []
  const safeDraft = draft || {}
  const safeTotals = totals || { totalAmount: 0, totalQty: 0, unitPriceAverage: 0 }
  const { previewZoom, tableDensity, fontScale } = useTemplatePreviewSettings()
  const previewCardRef = useRef(null)

  const rows = useMemo(() => previewRows(safeItems, template), [safeItems, template])

  const makeFileName = () => {
    const documentNumber = template === 'invoice'
      ? (safeDraft.invoiceNo || '')
      : template === 'purchaseOrder'
        ? (safeDraft.poNo || '')
        : (safeDraft.quotationNo || '')

    const normalized = String(documentNumber || title || 'preview')
      .replace(/[<>:"/\\|?*]+/g, '-')
      .replace(/\s+/g, '_')
      .replace(/^-+/, '')
      .slice(0, 120)

    return `${normalized || 'preview'}.pdf`
  }

  const handleDownloadWysiwyg = async () => {
    if (typeof onDownload !== 'function') {
      onClose?.()
      return
    }

    if (!previewCardRef.current) {
      onClose?.()
      return
    }

    // Keep the modal mounted until the export finishes — closing first would
    // unmount the preview element before its HTML can be captured.
    try {
      await onDownload(previewCardRef.current, makeFileName())
    } catch (error) {
      console.error('WYSIWYG preview export failed:', error)
    } finally {
      onClose?.()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4" onClick={onClose}>
      <div
        className="mx-auto flex h-full w-full max-w-[980px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 px-5 py-3 text-white">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Template Preview</p>
            <h2 className="text-lg font-semibold">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadWysiwyg}
              className="rounded-md border border-white/20 px-3 py-1 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Download
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-white/20 px-3 py-1 text-sm font-medium text-white transition hover:bg-white/10"
            >
              Close
            </button>
          </div>
        </div>
        <div className={`template-preview-theme template-density-${tableDensity} flex-1 overflow-auto bg-slate-200/40 p-3 md:p-4`} style={{ '--template-font-scale': fontScale }}>
          <div ref={previewCardRef} className="preview-card preview-card-document mx-auto" style={{ width: '210mm', minWidth: '210mm', zoom: previewZoom }}>
            {template === 'purchaseOrder' && (
              <PurchaseOrderPreview draft={safeDraft} items={safeItems} totals={safeTotals} />
            )}
            {template === 'quotation' && (
              <QuotationPreview draft={safeDraft} rows={rows} totals={safeTotals} />
            )}
            {template === 'invoice' && (
              <InvoicePreview draft={safeDraft} rows={rows} totals={safeTotals} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
